import React, { FC, ReactElement, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Button } from 'shared/ui/Button';
import { Modal } from 'shared/ui/Modal';
import { TextArea } from 'shared/ui/TextArea';
import { showNotification } from 'app/providers/NotificationsProvider';
import { Payment, PaymentItem, PaymentMethod, useCreditAndRefundPaymentMutation } from 'entities/Payment';
import { Select } from 'shared/ui/Select';
import { Form } from 'antd';
import { InputNumber } from 'shared/ui/InputNumber';
import { useGetCurrencySymbol } from 'app/appState';
import { WarnTag } from 'shared/ui/WarnTag';
import { useCheckPermission } from 'shared/utils/hooks/useCheckPermission';
import { UserPermissions } from 'entities/User';
import { Checkbox } from 'shared/ui/Checkbox';
import { Deposit } from 'entities/Deposit';
import { BulletsTable } from 'shared/ui/BulletsTable';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { InvoiceItemType } from 'entities/Invoice';

interface CreditAndRefundPaymentProps {
  payment: Payment;
  action: ReactElement;
  showIsDepositPayment?: boolean;
  isClosedContract: boolean;
  deposit?: Deposit;
}

const getItemMaxRefundAmount = (item: PaymentItem): number => {
  if (item.creditNoteItem) {
    return Math.min(item.maxRefundAmount ?? Infinity, item.creditNoteItem.creditedAmount);
  }
  return item.maxRefundAmount ?? item.amount;
};

export const CreditAndRefundPayment: FC<CreditAndRefundPaymentProps> = memo((props) => {
  const { payment, action, showIsDepositPayment, isClosedContract, deposit } = props;

  const [isOpenedModal, setOpenedModal] = useState(false);
  const [isDepositRefund, setIsDepositRefund] = useState<boolean>(false);
  const [refundAll, setRefundAll] = useState(true);
  const [paymentItemsToRefund, setPaymentItemsToRefund] = useState<Record<string, number>>({});

  const isForbidden = useCheckPermission(
    isClosedContract ? UserPermissions.REFUND_CREDITED_PAYMENTS : UserPermissions.REFUND_ACTIVE_PAYMENTS,
  );

  const [form] = Form.useForm();
  const currencySymbol = useGetCurrencySymbol();
  const { t } = useAppTranslation(['contracts', 'common']);

  const isDisabled = useMemo(() => {
    return isForbidden || payment.maxRefundAmount === 0;
  }, [isForbidden, payment.maxRefundAmount]);

  const maxRefundAmount = useMemo(
    () => Math.min(payment.maxRefundAmount ?? Infinity, payment?.creditNote?.creditedAmount || payment.amount),
    [payment.amount, payment?.creditNote?.creditedAmount, payment.maxRefundAmount],
  );

  const itemMaxRefundAmounts = useMemo(() => {
    return payment.paymentItems.reduce<Record<string, number>>((acc, item) => {
      acc[item.paymentItemId] = getItemMaxRefundAmount(item);
      return acc;
    }, {});
  }, [payment.paymentItems]);

  const depositChargesAmount = useMemo(() => {
    return deposit?.chargedDepositAmount || 0;
  }, [deposit]);

  const [creditAndRefundPayment, { isLoading }] = useCreditAndRefundPaymentMutation();

  const openModal = useCallback((): void => {
    setOpenedModal(true);
  }, []);

  const closeModal = useCallback((): void => {
    setOpenedModal(false);
    setPaymentItemsToRefund({});
    setRefundAll(true);
  }, []);

  useEffect(() => {
    if (isOpenedModal) {
      if (refundAll) {
        const defaults = payment.paymentItems.reduce<Record<string, number>>((acc, item) => {
          acc[item.paymentItemId] = itemMaxRefundAmounts[item.paymentItemId];
          return acc;
        }, {});
        setPaymentItemsToRefund(defaults);
      } else {
        setPaymentItemsToRefund({});
      }
    }
  }, [payment.paymentItems, isOpenedModal, refundAll, itemMaxRefundAmounts]);

  const totalRefundAmount = useMemo(() => {
    if (refundAll) return maxRefundAmount;
    return roundNumber(Object.values(paymentItemsToRefund).reduce((sum, val) => sum + (val || 0), 0));
  }, [refundAll, maxRefundAmount, paymentItemsToRefund]);

  const hasInvalidRefundAmounts = useMemo(() => {
    const values = Object.values(paymentItemsToRefund);
    if (!values.length || !values.some((v) => v)) return true;
    return payment.paymentItems.some((item) => {
      const amount = paymentItemsToRefund[item.paymentItemId] || 0;
      return amount > itemMaxRefundAmounts[item.paymentItemId];
    });
  }, [payment.paymentItems, paymentItemsToRefund, itemMaxRefundAmounts]);

  const handleCreditAndRefundPayment = useCallback(
    async (formData: { paymentMethod: string; description?: string }): Promise<void> => {
      try {
        if (payment) {
          const refundItems = Object.entries(paymentItemsToRefund)
            .filter(([_, amount]) => amount)
            .map(([paymentItemId, amount]) => ({ paymentItemId, amount }));

          await creditAndRefundPayment({
            paymentId: payment.paymentId,
            paymentMethod: formData.paymentMethod,
            description: formData.description,
            refundItems,
            isDepositRefund,
          });
          showNotification('info', t('Success'), t('Payment has been successfully credited and refunded'));
        }
      } catch (error: CustomAny) {
        showNotification('error', t('Error', { ns: 'common' }), t('Error when refunded payment'));
      } finally {
        closeModal();
      }
    },
    [payment, creditAndRefundPayment, isDepositRefund, paymentItemsToRefund, t, closeModal],
  );

  const paymentMethodOptions = [
    {
      label: t('Online payment'),
      value: PaymentMethod.ONLINE_PAYMENT,
    },
    // {
    //   label: t('Bank transfer'),
    //   value: PaymentMethod.BANK_TRANSFER,
    // },
    // {
    //   label: t('Cash'),
    //   value: PaymentMethod.CASH,
    // },
    // {
    //   label: t('Other'),
    //   value: PaymentMethod.OTHER,
    // },
  ];

  return (
    <>
      {React.cloneElement(action, { onClick: openModal })}

      <Modal className="max-w-[900px]" width={900} isOpen={isOpenedModal} onClose={closeModal}>
        <Form form={form} layout="vertical" onFinish={handleCreditAndRefundPayment} className="flex flex-col" disabled={isForbidden}>
          <div className="font-semibold text-xl mb-3">{t('Refund payment')}</div>

          {Number.isFinite(payment.maxRefundAmount) && (
            <div className="mt-2 mb-2">
              <WarnTag textSize="medium" disableIcon>
                {showIsDepositPayment && deposit
                  ? t(
                      'This payment is related with deposit that has {{depositChargesAmount}} {{currencySymbol}} charges, that restrict maximum refund amount for this payment to {{maxRefundAmount}}',
                      {
                        depositChargesAmount,
                        currencySymbol,
                        maxRefundAmount: payment.maxRefundAmount,
                      },
                    )
                  : t('This payment has max return amount {{amount}} {{currencySymbol}}', {
                      amount: payment.maxRefundAmount,
                      currencySymbol,
                    })}
              </WarnTag>
            </div>
          )}

          <div className="text-primaryLight font-normal">
            {t('Are you sure you want to refund this payment? You must select payment method!')}
          </div>

          {isClosedContract && !payment.creditNote && (
            <div className="mt-2 mb-2">
              <WarnTag textSize="medium" disableIcon>
                {t('After this payment refund related invoice will become active')}
              </WarnTag>
            </div>
          )}

          <div className="flex flex-wrap gap-10 my-5">
            <div className="max-w-[350px] flex-1">
              <Form.Item
                label={t('Select payment method')}
                name="paymentMethod"
                rules={[{ required: true, message: `${t('Please, select payment method')}!` }]}
              >
                <Select options={paymentMethodOptions} placeholder={t('Select payment method')} bordered />
              </Form.Item>

              <Form.Item label={t('Enter payment amount')}>
                <InputNumber value={totalRefundAmount} placeholder={t('Enter payment amount')} bordered isDisabled />
              </Form.Item>

              <div className="mb-4">
                <Checkbox name="refundAll" checked={refundAll} onChange={setRefundAll}>
                  <span className="text-m">{t('Refund all')}</span>
                </Checkbox>
              </div>

              {showIsDepositPayment && isClosedContract && (
                <div className="mb-5">
                  <Checkbox name="isDepositRefund" checked={isDepositRefund} onChange={setIsDepositRefund}>
                    <span className="text-m">{t('Deposit refund')}</span>
                  </Checkbox>
                </div>
              )}

              <Form.Item name="description">
                <TextArea placeholder={t('Add your description...')} rows={4} bordered />
              </Form.Item>
            </div>

            <div className="max-w-[500px] min-w-[300px] flex-1">
              <div className="font-normal text-xl mb-3">{t('Payment items')}</div>

              {payment.paymentItems.map((item) => {
                const itemMaxRefund = itemMaxRefundAmounts[item.paymentItemId];
                const rows = [
                  {
                    label: t('Amount'),
                    value: `${roundNumber(item.amount)} ${currencySymbol}`,
                    emptyValueSymbol: `0 ${currencySymbol}`,
                  },
                ];

                rows.push({
                  label: t('Credited'),
                  value: `${roundNumber(item.creditNoteItem?.creditedAmount || 0)} ${currencySymbol}`,
                  emptyValueSymbol: `0 ${currencySymbol}`,
                });

                rows.push({
                  label: t('Available refund'),
                  value: `${roundNumber(itemMaxRefund)} ${currencySymbol}`,
                  emptyValueSymbol: `0 ${currencySymbol}`,
                });

                const isRentalItem = item.paymentItemType === InvoiceItemType.RENTAL_ITEM;

                const paymentItemType = isRentalItem
                  ? item.invoiceItem?.contractItem?.rentalItem?.rentalItemType || item.paymentItemType
                  : item.paymentItemType;

                return (
                  <div className="border border-secondary rounded-lg my-2 p-4" key={item.paymentItemId}>
                    <div className="font-semibold text-lg mb-2">{t(paymentItemType)}</div>
                    <div className="flex flex-row items-end">
                      <div className="w-[70%]">
                        <BulletsTable theme="clear" rows={rows} />
                      </div>
                      <div className="w-[30%]">
                        <InputNumber
                          onChange={(value) => {
                            setPaymentItemsToRefund((prev) => ({ ...prev, [item.paymentItemId]: value || 0 }));
                          }}
                          value={paymentItemsToRefund[item.paymentItemId]}
                          placeholder={t('Refund amount')}
                          status={paymentItemsToRefund[item.paymentItemId] > itemMaxRefund ? 'error' : undefined}
                          isDisabled={refundAll || roundNumber(itemMaxRefund) === 0}
                          bordered
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button theme="secondary" onClick={closeModal}>
              {t('Cancel', { ns: 'common' })}
            </Button>

            <Button type="submit" isLoading={isLoading} isDisabled={isDisabled || hasInvalidRefundAmounts}>
              {t('Save', { ns: 'common' })}
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
});
